// Phase 326/448/490: Core Web Vitals Monitoring with Real User Monitoring (RUM)
// Tracks LCP, FID, CLS, INP, TTFB and sends to GA4 with alert thresholds
// Phase 490: Added page-specific tracking and aggregation

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface WebVitalMetric {
  name: 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType?: string;
  pagePath?: string;
  pageType?: 'home' | 'city' | 'blog' | 'service' | 'other';
}

// Thresholds based on Google's recommendations
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  FID: { good: 100, poor: 300 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
};

function getRating(name: WebVitalMetric['name'], value: number): WebVitalMetric['rating'] {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

// Determine page type for aggregation
function getPageType(path: string): WebVitalMetric['pageType'] {
  if (path === '/') return 'home';
  if (path.startsWith('/roofing/')) return 'city';
  if (path.startsWith('/blog/')) return 'blog';
  if (path.startsWith('/services/') || ['/roof-repair', '/roof-replacement', '/emergency', '/leak-detection'].includes(path)) return 'service';
  return 'other';
}

// Send metric to GA4 with page context
function sendToGA4(metric: WebVitalMetric) {
  if (typeof window !== 'undefined' && window.gtag) {
    const pagePath = window.location.pathname;
    const pageType = getPageType(pagePath);
    
    window.gtag('event', 'web_vitals', {
      event_category: 'Web Vitals',
      event_label: metric.name,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_rating: metric.rating,
      metric_delta: metric.delta,
      page_path: pagePath,
      page_type: pageType,
      non_interaction: true,
    });
  }
}

// Alert thresholds - trigger warnings in dev mode
const ALERT_THRESHOLDS = {
  LCP: 2500,  // 2.5s - Google's "good" threshold
  CLS: 0.1,   // Cumulative Layout Shift
  FID: 100,   // First Input Delay
  INP: 200,   // Interaction to Next Paint
  TTFB: 800,  // Time to First Byte
  FCP: 1800,  // First Contentful Paint
};

// Send to custom analytics endpoint and check thresholds
function sendToCustomAnalytics(metric: WebVitalMetric) {
  const threshold = ALERT_THRESHOLDS[metric.name];
  const exceeds = threshold && metric.value > threshold;
  
  // Log in development
  if (process.env.NODE_ENV === 'development') {
    const icon = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(`[CWV] ${icon} ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
    
    if (exceeds) {
      console.warn(`[CWV] ⚠️ ${metric.name} exceeds threshold: ${metric.value.toFixed(2)} > ${threshold}`);
    }
  }
  
  // Store in analytics_events table for production monitoring
  if (process.env.NODE_ENV === 'production' && exceeds) {
    storeMetricAlert(metric);
  }
}

// Store poor performance alerts in database
async function storeMetricAlert(metric: WebVitalMetric) {
  try {
    const visitorId = localStorage.getItem('visitor_id') || 'anonymous';
    
    await supabase.from('analytics_events').insert({
      visitor_id: visitorId,
      event_name: 'cwv_alert',
      page_path: window.location.pathname,
      event_params: {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_rating: metric.rating,
        threshold: ALERT_THRESHOLDS[metric.name],
        user_agent: navigator.userAgent.substring(0, 200),
      },
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.debug('[CWV] Failed to store alert:', error);
  }
}

export function useCoreWebVitals() {
  const metricsRef = useRef<Record<string, WebVitalMetric>>({});
  const reportedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    const reportMetric = (metric: WebVitalMetric) => {
      // Only report each metric once per page load
      if (reportedRef.current.has(metric.name)) return;
      reportedRef.current.add(metric.name);
      
      metricsRef.current[metric.name] = metric;
      sendToGA4(metric);
      sendToCustomAnalytics(metric);
    };

    // Use PerformanceObserver for Core Web Vitals
    const observers: PerformanceObserver[] = [];

    // Largest Contentful Paint (LCP)
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
        
        if (lastEntry) {
          const value = lastEntry.startTime;
          reportMetric({
            name: 'LCP',
            value,
            rating: getRating('LCP', value),
            delta: value,
            id: `lcp-${Date.now()}`,
          });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);
    } catch (e) {
      // LCP not supported
    }

    // First Input Delay (FID) / Interaction to Next Paint (INP)
    try {
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        
        entries.forEach((entry: PerformanceEntry & { processingStart?: number; startTime?: number }) => {
          if (entry.processingStart !== undefined && entry.startTime !== undefined) {
            const value = entry.processingStart - entry.startTime;
            
            // Only report FID if it's the first input
            if (!reportedRef.current.has('FID')) {
              reportMetric({
                name: 'FID',
                value,
                rating: getRating('FID', value),
                delta: value,
                id: `fid-${Date.now()}`,
              });
            }
          }
        });
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
      observers.push(fidObserver);
    } catch (e) {
      // FID not supported
    }

    // Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      let sessionValue = 0;
      let sessionEntries: PerformanceEntry[] = [];
      
      const clsObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries() as (PerformanceEntry & { hadRecentInput?: boolean; value?: number })[];
        
        entries.forEach((entry) => {
          if (!entry.hadRecentInput && entry.value !== undefined) {
            const firstSessionEntry = sessionEntries[0] as PerformanceEntry & { startTime: number } | undefined;
            const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as PerformanceEntry & { startTime: number } | undefined;
            
            if (
              sessionValue &&
              firstSessionEntry &&
              lastSessionEntry &&
              (entry as PerformanceEntry & { startTime: number }).startTime - lastSessionEntry.startTime < 1000 &&
              (entry as PerformanceEntry & { startTime: number }).startTime - firstSessionEntry.startTime < 5000
            ) {
              sessionValue += entry.value;
              sessionEntries.push(entry);
            } else {
              sessionValue = entry.value;
              sessionEntries = [entry];
            }
            
            if (sessionValue > clsValue) {
              clsValue = sessionValue;
            }
          }
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObserver);

      // Report CLS on page hide
      const reportCLS = () => {
        if (clsValue > 0 && !reportedRef.current.has('CLS')) {
          reportMetric({
            name: 'CLS',
            value: clsValue,
            rating: getRating('CLS', clsValue),
            delta: clsValue,
            id: `cls-${Date.now()}`,
          });
        }
      };

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportCLS();
        }
      });

      window.addEventListener('pagehide', reportCLS);
    } catch (e) {
      // CLS not supported
    }

    // First Contentful Paint (FCP)
    try {
      const fcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntriesByName('first-contentful-paint');
        if (entries.length > 0) {
          const entry = entries[0] as PerformanceEntry & { startTime: number };
          const value = entry.startTime;
          
          reportMetric({
            name: 'FCP',
            value,
            rating: getRating('FCP', value),
            delta: value,
            id: `fcp-${Date.now()}`,
          });
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
      observers.push(fcpObserver);
    } catch (e) {
      // FCP not supported
    }

    // Time to First Byte (TTFB)
    try {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navigationEntries.length > 0) {
        const value = navigationEntries[0].responseStart;
        
        reportMetric({
          name: 'TTFB',
          value,
          rating: getRating('TTFB', value),
          delta: value,
          id: `ttfb-${Date.now()}`,
        });
      }
    } catch (e) {
      // TTFB not supported
    }

    // Cleanup
    return () => {
      observers.forEach((observer) => {
        try {
          observer.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
      });
    };
  }, []);

  return metricsRef.current;
}

export default useCoreWebVitals;
